import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import { Box, Chip, Typography } from "@mui/material";
import type { KksOccurrence } from "@shared/types/domain";
import { useCallback, useRef } from "react";
import { buildOccurrenceUrl } from "../utils/kks";
import { StatusChip } from "./StatusChip";

/** Which occurrence table a row/header belongs to. Determines the column set. */
export type OccurrenceVariant = "anomalies" | "search";

interface OccColumn {
  key: string;
  label: string;
  defaultWidth: number;
}

const COL_PLANT: OccColumn = {
  key: "plant",
  label: "Plant",
  defaultWidth: 160,
};
const COL_SYSTEM: OccColumn = {
  key: "system",
  label: "System",
  defaultWidth: 160,
};
const COL_SUBSYSTEM: OccColumn = {
  key: "sub_system",
  label: "Subsystem",
  defaultWidth: 110,
};
const COL_PID: OccColumn = { key: "pid", label: "P&ID", defaultWidth: 170 };
const COL_STATUS: OccColumn = {
  key: "status",
  label: "Status",
  defaultWidth: 150,
};
const COL_KKS: OccColumn = { key: "kks", label: "KKS", defaultWidth: 170 };
const COL_VERIFIED: OccColumn = {
  key: "verified",
  label: "Verified",
  defaultWidth: 80,
};
const COL_COMPONENT: OccColumn = {
  key: "component",
  label: "Component",
  defaultWidth: 140,
};
const COL_SPOF: OccColumn = { key: "spof", label: "SPOF", defaultWidth: 70 };
const COL_IMPACT: OccColumn = {
  key: "impact",
  label: "Impact",
  defaultWidth: 70,
};

const ANOMALY_COLUMNS: OccColumn[] = [
  COL_PLANT,
  COL_SYSTEM,
  COL_SUBSYSTEM,
  COL_PID,
  COL_KKS,
  COL_COMPONENT,
  COL_SPOF,
  COL_IMPACT,
];

const SEARCH_COLUMNS: OccColumn[] = [
  COL_PLANT,
  COL_SYSTEM,
  COL_SUBSYSTEM,
  COL_PID,
  COL_STATUS,
  COL_KKS,
  COL_VERIFIED,
  COL_COMPONENT,
  COL_SPOF,
  COL_IMPACT,
];

export function occurrenceColumns(variant: OccurrenceVariant): OccColumn[] {
  return variant === "search" ? SEARCH_COLUMNS : ANOMALY_COLUMNS;
}

export function defaultOccurrenceColWidths(
  variant: OccurrenceVariant,
): number[] {
  return occurrenceColumns(variant).map((c) => c.defaultWidth);
}

interface OccurrenceHeaderRowProps {
  variant: OccurrenceVariant;
  colWidths: number[];
  onResize: (index: number, delta: number) => void;
  onResizeEnd: () => void;
}

export function OccurrenceHeaderRow({
  variant,
  colWidths,
  onResize,
  onResizeEnd,
}: OccurrenceHeaderRowProps) {
  const columns = occurrenceColumns(variant);
  const dragIndex = useRef<number | null>(null);
  const lastX = useRef(0);

  const startDrag = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      dragIndex.current = index;
      lastX.current = e.clientX;

      const onMouseMove = (ev: MouseEvent) => {
        if (dragIndex.current === null) return;
        const delta = ev.clientX - lastX.current;
        lastX.current = ev.clientX;
        onResize(dragIndex.current, delta);
      };

      const onMouseUp = () => {
        dragIndex.current = null;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        onResizeEnd();
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [onResize, onResizeEnd],
  );

  const onResizeKeyDown = useCallback(
    (index: number) => (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onResize(index, -10);
        onResizeEnd();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onResize(index, 10);
        onResizeEnd();
      }
    },
    [onResize, onResizeEnd],
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: colWidths.map((w) => `${w}px`).join(" "),
        alignItems: "center",
        gap: 1,
        px: 3,
        py: 0.6,
        bgcolor: "#F0F2F5",
        borderBottom: "1px solid #DEE3EA",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {columns.map((col, i) => (
        <Box
          key={col.key}
          sx={{ position: "relative", display: "flex", alignItems: "center" }}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {col.label}
          </Typography>
          <Box
            onMouseDown={startDrag(i)}
            onKeyDown={onResizeKeyDown(i)}
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${col.label} column`}
            tabIndex={0}
            sx={{
              position: "absolute",
              top: -4,
              bottom: -4,
              right: -8,
              width: 8,
              display: "flex",
              justifyContent: "center",
              cursor: "col-resize",
              zIndex: 2,
              "&:hover > .resize-handle-bar, &:focus-visible > .resize-handle-bar":
                {
                  bgcolor: "#009DE0",
                },
            }}
          >
            <Box
              className="resize-handle-bar"
              sx={{
                width: "2px",
                height: "100%",
                bgcolor: "#C4CBD4",
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function renderCell(occ: KksOccurrence, key: string) {
  switch (key) {
    case "plant":
      return (
        <Typography variant="caption" noWrap title={occ.plant_name}>
          {occ.plant_name}
        </Typography>
      );
    case "system":
      return (
        <Typography variant="caption" noWrap title={occ.system_name}>
          {occ.system_name}
        </Typography>
      );
    case "sub_system":
      return (
        <Typography variant="caption" noWrap title={occ.sub_system}>
          {occ.sub_system || "—"}
        </Typography>
      );
    case "pid":
      return (
        <Typography variant="caption" noWrap title={occ.pid_filename}>
          {occ.pid_filename}
        </Typography>
      );
    case "status":
      return occ.pid_status ? (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
        >
          <StatusChip status={occ.pid_status} />
          {occ.pid_locked && (
            <LockIcon
              sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }}
              titleAccess="Locked"
            />
          )}
        </Box>
      ) : (
        <Typography variant="caption">—</Typography>
      );
    case "kks":
      return (
        <Typography
          variant="caption"
          noWrap
          sx={{ fontFamily: "monospace" }}
          title={occ.kks_text}
        >
          {occ.kks_text}
        </Typography>
      );
    case "verified":
      return occ.verified ? (
        <CheckCircleIcon
          sx={{ fontSize: 16, color: "#43A047" }}
          titleAccess="Verified"
        />
      ) : (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          —
        </Typography>
      );
    case "component":
      return (
        <Typography variant="caption" noWrap title={occ.component}>
          {occ.component || "—"}
        </Typography>
      );
    case "spof":
      return (
        <Chip
          label={occ.spof ? "SPOF" : "—"}
          size="small"
          color={occ.spof ? "error" : "default"}
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 18, px: 0.5 }}
        />
      );
    case "impact":
      return (
        <Typography
          variant="caption"
          sx={{ textAlign: "center" }}
          title="Impact"
        >
          {occ.impact}
        </Typography>
      );
    default:
      return null;
  }
}

interface OccurrenceRowProps {
  occurrence: KksOccurrence;
  variant: OccurrenceVariant;
  colWidths: number[];
  onClick: () => void;
}

export function OccurrenceRow({
  occurrence: occ,
  variant,
  colWidths,
  onClick,
}: OccurrenceRowProps) {
  const columns = occurrenceColumns(variant);

  const handleClick = (e: React.MouseEvent) => {
    // Let the browser handle modified clicks (middle-click, ctrl/cmd/shift+click,
    // right-click → "open in new tab") by following the href natively.
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    onClick();
  };

  return (
    <Box
      component="a"
      href={buildOccurrenceUrl(occ, variant)}
      onClick={handleClick}
      sx={{
        display: "grid",
        gridTemplateColumns: colWidths.map((w) => `${w}px`).join(" "),
        alignItems: "center",
        gap: 1,
        px: 3,
        py: 0.6,
        cursor: "pointer",
        bgcolor: "#fff",
        borderTop: "1px solid #F0F2F5",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "#EEF6FF" },
      }}
    >
      {columns.map((col) => (
        <Box key={col.key} sx={{ minWidth: 0, overflow: "hidden" }}>
          {renderCell(occ, col.key)}
        </Box>
      ))}
    </Box>
  );
}
